import React, { useState, useCallback } from "react"
import PropTypes from "prop-types"
import { useIntl, navigate } from "gatsby-plugin-intl"

import usePortfolioNavigationContent from "utils/usePortfolioNavigationContent"
import paths from "utils/paths"
import { useClipboard } from "use-clipboard-copy"
import useWindowSize from "utils/useWindowSize"
import bouncingSubject from "utils/bouncingSubject"
import trackCustomEventTrigger from "utils/trackCustomEventTrigger"
import trackingCategoriesHelper from "utils/trackingCategoriesHelper"

import BtnLink from "components/atoms/BtnLink"
import H from "components/atoms/H"
import Modal from "components/atoms/Modal"
import NavList from "components/molecules/NavList"
import SubscriptionForm from "components/molecules/SubscriptionForm"

import ChevronLeft from "assets/icons/chevron-left.svg"
import Subscribe from "assets/icons/subscribe.svg"
import Share from "assets/icons/share.svg"

import theme from "styles/theme"

import Button from "components/atoms/Button"
import {
  backButtonStyles,
  linkButtonStyles,
  headingStyles,
  StyledNavWrapper,
  StyledContainer,
  StyledIcon,
  StyledButtonWrapper,
  StyledLocationIcon,
  StyledLocationIconRect,
  addressLinkStyles,
  StyledTooltip,
  subscribeButtonStyles,
} from "./PortfolioNavigation.styles"

const showSubscribe = true

const PortfolioNavigation = ({
  projectData,
  location,
  bannerHeight,
  backButtonProps,
}) => {
  const [isSubscriptionModalOpen, setSubscriptionModalStatus] = useState(false)
  const { formatMessage } = useIntl()
  const clipboard = useClipboard()
  const size = useWindowSize()
  const { name, slug, id } = projectData

  const {
    leftTabColumnAddressLine1,
    leftTabColumnAddressLine2,
    leftTabColumnAddressLine3,
  } = projectData.location || {}

  const goBack = () => {
    navigate(
      backButtonProps?.pathname
        ? backButtonProps.pathname
        : `${paths.portfolio}`
    )
    trackCustomEventTrigger({
      category: trackingCategoriesHelper.PROJECT_NAVIGATION,
      action: "Click",
      label: `${trackingCategoriesHelper.PROJECT_NAVIGATION} button(Back)`,
    })
  }
  const openSubscriptionModal = () => {
    if (showSubscribe) {
      setSubscriptionModalStatus(true)
      trackCustomEventTrigger({
        category: trackingCategoriesHelper.PROJECT_NAVIGATION,
        action: "Click",
        label: `${trackingCategoriesHelper.PROJECT_NAVIGATION} button(Subscribe modal)`,
      })
    }
  }
  const copyToClipboard = useCallback(
    (clipboardContent) => {
      clipboard.copy(clipboardContent)
    },
    [clipboard.copy]
  )
  const closeSubscriptionModal = () => {
    setSubscriptionModalStatus(false)
    trackCustomEventTrigger({
      category: trackingCategoriesHelper.SUBSCRIBE,
      action: "Click",
      label: `${trackingCategoriesHelper.SUBSCRIBE} button(Cancel)`,
    })
  }

  const renderNavigation = (
    <>
      <StyledContainer>
        <BtnLink
          linkStyles={backButtonStyles}
          replace
          onClick={goBack}
          data-testid="backButton"
        >
          <StyledIcon>
            <ChevronLeft />
          </StyledIcon>
          {backButtonProps?.label
            ? backButtonProps.label
            : formatMessage({ id: "pages.portfolio.navigation.backButton" })}
        </BtnLink>
        <H as="h1" extraStyles={headingStyles}>
          {name}
        </H>
        {projectData.location && (
          <>
            <BtnLink
              linkStyles={addressLinkStyles}
              data-testid="locationLink"
              to={
                location.pathname.includes("location")
                  ? null
                  : paths.portfolioProjectLocation(slug)
              }
              data-tip
              data-for="address_information"
              onMouseEnter={() => bouncingSubject.notify(true)}
              onMouseLeave={() => bouncingSubject.notify(false)}
              trackingOptions={{
                category: trackingCategoriesHelper.PROJECT_NAVIGATION,
                action: "Click",
                label: `${trackingCategoriesHelper.PROJECT_NAVIGATION} address link(location)`,
              }}
            >
              <StyledLocationIcon
                isActive={location.pathname.includes("location")}
              >
                <StyledLocationIconRect />
              </StyledLocationIcon>
              {`${leftTabColumnAddressLine1}${
                leftTabColumnAddressLine2
                  ? `, ${leftTabColumnAddressLine2}`
                  : ""
              }${
                leftTabColumnAddressLine3
                  ? `, ${leftTabColumnAddressLine3}`
                  : ""
              }`}
            </BtnLink>
            {!location.pathname.includes("location") && (
              <StyledTooltip
                id="address_information"
                place="top"
                effect="solid"
                type="light"
                hideAfter={10000}
              >
                {formatMessage({ id: "pages.portfolio.navigation.visit" })}
              </StyledTooltip>
            )}
          </>
        )}
        <StyledButtonWrapper>
          {showSubscribe && (
            <BtnLink
              linkStyles={linkButtonStyles}
              onClick={openSubscriptionModal}
              data-testid="openSubscriptionModalButton"
            >
              <StyledIcon>
                <Subscribe />
              </StyledIcon>
              {formatMessage({ id: "pages.portfolio.navigation.subscribe" })}
            </BtnLink>
          )}
          <Button
            extraStyles={subscribeButtonStyles}
            data-tip
            data-for="subscribe-button"
            data-event="click"
          >
            <StyledIcon>
              <Share />
            </StyledIcon>
            {formatMessage({ id: "pages.portfolio.navigation.share" })}
          </Button>
          <StyledTooltip
            id="subscribe-button"
            place="right"
            effect="solid"
            type="light"
            clickable
            customAfterShow={() => {
              copyToClipboard(window.location.href)
            }}
          >
            {formatMessage({
              id: "linkCopied",
            })}
          </StyledTooltip>
        </StyledButtonWrapper>
      </StyledContainer>

      <NavList
        navContent={usePortfolioNavigationContent(projectData, slug)}
        bannerHeight={bannerHeight}
      />
    </>
  )

  return (
    <>
      {size.width >= theme.breakpoints.medium ? (
        <StyledNavWrapper>{renderNavigation}</StyledNavWrapper>
      ) : (
        <>{renderNavigation}</>
      )}
      <Modal
        showModal={isSubscriptionModalOpen}
        handleCloseModal={closeSubscriptionModal}
      >
        <SubscriptionForm
          handleClose={closeSubscriptionModal}
          defaultProjectId={id}
        />
      </Modal>
    </>
  )
}

PortfolioNavigation.propTypes = {
  projectData: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    slug: PropTypes.string,
    location: PropTypes.shape({
      leftTabColumnAddressLine1: PropTypes.string,
      leftTabColumnAddressLine2: PropTypes.string,
      leftTabColumnAddressLine3: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  bannerHeight: PropTypes.number.isRequired,
  backButtonProps: PropTypes.shape({
    pathname: PropTypes.string,
    label: PropTypes.string,
  }),
}

PortfolioNavigation.defaultProps = {
  backButtonProps: null,
}

export default PortfolioNavigation
